import type { OverlayEvent } from '@shared/types'

import { OVERLAY_CONFIG } from '@shared/config'
import { isZoneId } from '@shared/lib/zone-layout'

import { CUSTOM_EFFECTS_TYPE } from './plugins/custom-effects'

// ── Долгоживущие наслаивающиеся типы ────────────────────────────────────────
// donation_alert/text_alert физически не перекрывают весь экран (в отличие от
// custom_effects) и не обязаны ждать общий лимит очереди наравне с короткими
// эффектами — иначе, например, длинный donation_alert-стек может надолго
// заблокировать слот для fireworks. Выносим их (как и зональные события) из-
// под общего лимита конкурентности — см. isQueueBypassEvent ниже.
const LONG_LIVED_OVERLAY_TYPES: ReadonlySet<string> = new Set([
  'donation_alert',
  'text_alert',
])

/**
 * Событие «зональное» — backend явно указал zone в payload (плоско или во
 * вложенном overlayConfig.zone, см. resolveEventZone/overlay.service.ts).
 * Такие события (poll/drawing/soundboard) стекуются в своей зоне через
 * ZoneLayoutManager и физически не конкурируют за экран с остальными —
 * поэтому не должны стоять в общей очереди effect-queue.
 */
export function isZonedEvent(event: OverlayEvent): boolean {
  const payload = event.payload as Record<string, unknown> | undefined
  if (!payload || typeof payload !== 'object') return false
  if (isZoneId(payload.zone)) return true
  const overlayConfig = payload.overlayConfig
  if (overlayConfig && typeof overlayConfig === 'object') {
    if (isZoneId((overlayConfig as Record<string, unknown>).zone)) return true
  }
  return false
}

/**
 * Событие, которое должно проходить очередь эффектов «в обход» лимита
 * QUEUE_MAX_CONCURRENT и режима exclusiveMode: зональные виджеты (у них своя
 * зона на экране — физически не пересекаются с другими), долгоживущие
 * наслаивающиеся алерты (donation_alert/text_alert — не должны надолго
 * занимать общий слот и блокировать короткие эффекты) и любые события, где
 * backend явно выставил BaseEvent.bypassQueue=true.
 *
 * bypassQueue — универсальный per-event флаг (по аналогии с уже существующим
 * priority для isExclusiveEvent), которым может воспользоваться любой плагин
 * с бэкенда: не нужно хардкодить конкретные event.type в overlay-репозитории,
 * достаточно проставить флаг на самом событии при его формировании на backend.
 *
 * ВАЖНО: custom_effects (см. isExclusiveEvent) никогда не bypass — он всегда
 * идёт через общий эксклюзивный механизм, но при этом сам по себе больше не
 * блокирует показ bypass-событий (см. store.ts: exclusiveMode проверяется
 * только относительно НЕ-bypass активных эффектов).
 */
export function isQueueBypassEvent(event: OverlayEvent): boolean {
  if (event.type === CUSTOM_EFFECTS_TYPE) return false
  return (
    isZonedEvent(event) ||
    LONG_LIVED_OVERLAY_TYPES.has(event.type) ||
    event.bypassQueue === true
  )
}

export function getEffectDuration(event: OverlayEvent): number {
  switch (event.type) {
    case 'fireworks':      return (event.payload.duration as number | undefined) ?? OVERLAY_CONFIG.FIREWORKS_DEFAULT_DURATION_MS
    case 'text_alert':     return (event.payload.duration as number | undefined) ?? OVERLAY_CONFIG.TEXT_ALERT_DEFAULT_DURATION_MS
    case 'donation_alert': return OVERLAY_CONFIG.DONATION_ALERT_DEFAULT_DURATION_MS
    case 'ping':           return 0
    default:
      // DynamicEvent: используем payload.duration если задан
      return (event.payload as { duration?: unknown }).duration as number | undefined
        ?? OVERLAY_CONFIG.TEXT_ALERT_DEFAULT_DURATION_MS
  }
}

export function isExclusiveEvent(event: OverlayEvent): boolean {
  // custom_effects всегда эксклюзивен: эффект не рендерится одновременно с другим
  // (см. план §2/§8 — защита от визуального хаоса). Backend priority-флаг не шлёт.
  return event.priority === true || event.type === CUSTOM_EFFECTS_TYPE
}
